/* /scan — parcours passager.
   Le QR porte le matricule du véhicule (/scan?v=<matricule>) et c'est une
   condition d'entrée : sans matricule dans l'URL, ou avec un matricule absent de
   la flotte, le parcours est bloqué — un scan que personne ne peut rattacher ne
   doit ni consommer un ticket ni entrer dans la paie des chauffeurs.
   La commune est demandée AVANT de charger le scan (ZonePicker) : position GPS
   si le passager l'accorde, sinon choix manuel, sinon rien. C'est la seule
   source qui donne la commune — l'IP ne descend jamais sous la ville.
   /scan/init n'est appelé qu'une fois la localisation tranchée, sinon on
   créerait deux ScanEvent et les statistiques de scan seraient doublées. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { applyBrand, loadBrandFonts } from "@/components/BrandStyleInjector";
import AdCarousel from "@/components/scan/AdCarousel";
import PrizeSlider from "@/components/scan/PrizeSlider";
import AdGate from "@/components/scan/AdGate";
import WinOptIn from "@/components/scan/WinOptIn";
import ZonePicker from "@/components/scan/ZonePicker";
import ScanSkeleton from "@/components/scan/ScanSkeleton";
import ScanBlocked from "@/components/scan/ScanBlocked";
import BrandLogo from "@/components/BrandLogo";
import { AlertTriangle, QrCode, Smartphone } from "lucide-react";
import { mergeTexts, DEFAULT_GATE_SECONDS } from "@/lib/defaults";
import { isMobilePhone } from "@/lib/device";

// Localisation retenue pour CE scan, gardée le temps de l'onglet : un
// rafraîchissement ne doit pas redemander la position. Une nouvelle course =
// un nouvel onglet = une nouvelle demande, ce qui est le comportement voulu.
const LOCATION_KEY = "gocab_scan_location";

function readStoredLocation() {
  try {
    const raw = sessionStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null; // navigation privée / stockage bloqué
  }
}

const GEO_OPTIONS = { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 };

export default function ScanPage({ brandOverride, fakeData, previewMode = false }) {
  const [searchParams] = useSearchParams();
  // Le QR encode le matricule du véhicule (/scan?v=…) : il sert à l'identité,
  // au ciblage des campagnes par catégorie de flotte et à la paie du chauffeur.
  const vehicleId = searchParams.get("v");

  // { zoneId } | { lat, lng } | { skipped: true } — null tant que non tranché.
  const [location, setLocation] = useState(readStoredLocation);
  const [geoStatus, setGeoStatus] = useState("idle"); // idle | loading | denied | error | unsupported

  const commitLocation = useCallback((next) => {
    setLocation(next);
    try {
      sessionStorage.setItem(LOCATION_KEY, JSON.stringify(next));
    } catch {
      /* stockage indisponible : on garde juste l'état React */
    }
  }, []);

  const requestGeo = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus("unsupported");
      return;
    }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => commitLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setGeoStatus(err?.code === err?.PERMISSION_DENIED ? "denied" : "error"),
      GEO_OPTIONS
    );
  }, [commitLocation]);

  // Lien sans matricule : on bloque ici, avant la commune et avant /scan/init.
  // La preview admin (console) et les données injectées en sont exemptées.
  const vehicleMissing = !fakeData && !previewMode && !vehicleId;

  // La preview admin et les données injectées court-circuitent le sélecteur.
  const needsLocation = !fakeData && !previewMode && !vehicleMissing && !location;

  const scanParams = useMemo(() => {
    const p = {};
    if (vehicleId) p.v = vehicleId;
    if (location?.zoneId) p.zoneId = location.zoneId;
    else if (typeof location?.lat === "number") {
      p.lat = location.lat;
      p.lng = location.lng;
    }
    return p;
  }, [vehicleId, location]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["scan-init", scanParams],
    queryFn: async () => (await api.get("/scan/init", { params: scanParams })).data,
    enabled: !fakeData && !needsLocation && !vehicleMissing,
    staleTime: 30_000,
  });

  // Communes proposées en repli. /zones ne logge aucun ScanEvent, contrairement
  // à /scan/init : on peut l'appeler avant que le parcours ne démarre.
  const { data: pickerZones = [], isLoading: zonesLoading } = useQuery({
    queryKey: ["public-zones"],
    queryFn: async () => (await api.get("/zones")).data,
    enabled: needsLocation,
    staleTime: 5 * 60_000,
  });

  // Le branding arrive normalement avec /scan/init ; le sélecteur s'affichant
  // avant, on le récupère à part pour ne pas montrer un premier écran nu.
  const { data: pickerBranding } = useQuery({
    queryKey: ["branding-active"],
    queryFn: async () => (await api.get("/branding/active")).data,
    enabled: needsLocation && !brandOverride,
    staleTime: 5 * 60_000,
  });

  const payload = fakeData || data;
  const branding = brandOverride || payload?.branding || pickerBranding || null;
  const texts = mergeTexts(branding?.texts);
  const gateSeconds = branding?.adGateSeconds || DEFAULT_GATE_SECONDS;

  // Verrou de vue de campagne : le formulaire reste bloqué le temps que le
  // client voie la pub (barre de progression simple). Exempté en preview.
  const [gateOpen, setGateOpen] = useState(previewMode);
  useEffect(() => {
    if (previewMode) setGateOpen(true);
  }, [previewMode]);

  // Sponsor Officiel du Mois — the platform takes the sponsor's colors while
  // GoCab Boost branding stays present (co-branding).
  const monthlySponsor = payload?.monthlySponsor || null;
  const monthlyDraw = payload?.monthlyDraw || null;

  useEffect(() => {
    const base = brandOverride || payload?.branding || pickerBranding || null;
    const takeover =
      monthlySponsor && (monthlySponsor.primaryColor || monthlySponsor.secondaryColor)
        ? {
            ...(base || {}),
            primaryColor: monthlySponsor.primaryColor || base?.primaryColor,
            secondaryColor: monthlySponsor.secondaryColor || base?.secondaryColor,
          }
        : base;
    if (takeover) {
      applyBrand(takeover);
      loadBrandFonts(takeover);
    }
  }, [payload, brandOverride, pickerBranding, monthlySponsor]);

  const titleText = texts.scanHeroTitle || "";
  const accentWord = texts.scanHeroTitleAccent;
  let titleBefore = titleText;
  let titleAccent = "";
  let titleAfter = "";
  if (accentWord && titleText.toLowerCase().includes(accentWord.toLowerCase())) {
    const idx = titleText.toLowerCase().indexOf(accentWord.toLowerCase());
    titleBefore = titleText.slice(0, idx);
    titleAccent = titleText.slice(idx, idx + accentWord.length);
    titleAfter = titleText.slice(idx + accentWord.length);
  }

  // Parcours passager réservé au téléphone mobile. Le mode preview (console
  // admin, sur desktop) et les données injectées en sont exemptés.
  if (!previewMode && !fakeData && !isMobilePhone()) {
    return (
      <ScanBlocked
        icon={Smartphone}
        testId="scan-mobile-only"
        title="Ouvrez cette page sur votre téléphone"
        message="Cette page est réservée aux téléphones mobiles. Scannez à nouveau le QR code GoCab depuis votre smartphone pour participer."
      />
    );
  }

  // Lien ouvert sans matricule (QR générique, URL recopiée, lien partagé).
  if (vehicleMissing) {
    return (
      <ScanBlocked
        icon={QrCode}
        testId="scan-vehicle-missing"
        title="Scannez le QR code du véhicule"
        message="Ce lien n'indique aucun véhicule. Scannez le QR code collé dans la voiture — celui qui porte son immatriculation — pour participer."
      />
    );
  }

  // Localisation demandée avant tout : /scan/init n'est pas encore parti, donc
  // aucun ScanEvent n'existe à ce stade.
  if (needsLocation) {
    return (
      <div
        className="relative min-h-[100dvh] w-full mx-auto bg-background"
        style={{ maxWidth: 480 }}
      >
        <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border">
          <div className="flex items-center px-4 py-3">
            <BrandLogo logoUrl={branding?.logoUrl} className="h-8" />
          </div>
        </header>

        <ZonePicker
          zones={pickerZones}
          loading={zonesLoading}
          geoStatus={geoStatus}
          onUseGeo={requestGeo}
          onPick={(z) => commitLocation({ zoneId: z.id })}
          onSkip={() => commitLocation({ skipped: true })}
        />

        <footer className="px-4 pt-2 pb-8 text-center">
          <div className="text-[10px] text-muted-foreground leading-relaxed">
            Votre position sert uniquement à déterminer votre commune, afin de vous
            proposer les lots qui y sont disponibles.
          </div>
        </footer>
      </div>
    );
  }

  // Matricule absent de la flotte : autocollant d'un véhicule retiré, ou
  // matricule bricolé dans l'URL. Le backend n'a enregistré aucun scan.
  if (payload?.vehicleRequired) {
    return (
      <ScanBlocked
        icon={AlertTriangle}
        testId="scan-vehicle-unknown"
        title="Véhicule non reconnu"
        message="Le matricule de ce QR code ne correspond à aucun véhicule GoCab. Signalez-le à votre chauffeur."
      />
    );
  }

  // QR code désactivé par l'admin pour ce véhicule → parcours bloqué.
  if (payload?.qrDisabled) {
    return (
      <ScanBlocked
        icon={AlertTriangle}
        testId="scan-qr-disabled"
        title="QR code indisponible"
        message="Ce QR code n'est pas actif pour le moment. Adressez-vous à votre chauffeur ou réessayez plus tard."
      />
    );
  }

  return (
    <div
      className="relative min-h-[100dvh] w-full mx-auto bg-background"
      style={{ maxWidth: 480 }}
    >
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2.5">
            <BrandLogo
              logoUrl={branding?.logoUrl}
              className="h-8"
              data-testid="sponsor-logo"
            />
          </div>
          {monthlySponsor?.logoUrl && (
            <img
              src={monthlySponsor.logoUrl}
              alt={monthlySponsor.name}
              className="h-6 max-w-[80px] object-contain rounded-md bg-white/70 p-0.5 border border-border"
              data-testid="monthly-sponsor-header-logo"
            />
          )}
        </div>
      </header>

      <main className="px-4 py-5 space-y-6 pb-24">
        {isLoading && !fakeData ? (
          <ScanSkeleton />
        ) : error ? (
          <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6 text-center">
            <AlertTriangle className="h-6 w-6 text-destructive mx-auto" />
            <div className="mt-2 font-semibold">Erreur de chargement</div>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 h-11 px-5 rounded-full bg-foreground text-background font-medium"
              data-testid="scan-retry-btn"
            >
              Réessayer
            </button>
          </div>
        ) : (
          payload && (
            <>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-muted-foreground">
                  {texts.scanHeroEyebrow}
                </div>
                <h1 className="font-heading text-2xl font-bold leading-tight mt-1.5">
                  {titleBefore}
                  {titleAccent && (
                    <span style={{ color: "var(--color-brand)" }}>{titleAccent}</span>
                  )}
                  {titleAfter}
                </h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  {texts.scanHeroSubtitle}
                </p>
              </motion.div>

              {/* Bandeau Grand Tirage — sponsor officiel du mois */}
              {monthlyDraw && monthlySponsor && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 }}
                  data-testid="monthly-sponsor-banner"
                  className="relative overflow-hidden rounded-2xl border-2"
                  style={{
                    borderColor: monthlySponsor.primaryColor || "var(--color-brand)",
                    background: "hsl(var(--card))",
                  }}
                >
                  {monthlySponsor.bannerUrl && (
                    <img
                      src={monthlySponsor.bannerUrl}
                      alt=""
                      className="w-full h-16 object-cover"
                    />
                  )}
                  <div className="flex items-center gap-2.5 px-3 py-2">
                    {monthlySponsor.logoUrl && (
                      <img
                        src={monthlySponsor.logoUrl}
                        alt={monthlySponsor.name}
                        className="h-8 w-8 object-contain rounded-lg bg-white p-1 border border-border"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div
                        className="font-heading font-bold text-xs leading-tight"
                        style={{ color: monthlySponsor.primaryColor || "var(--color-brand)" }}
                      >
                        {monthlyDraw.drawTitle ||
                          `Grand Tirage Mensuel by ${monthlySponsor.name}`}
                      </div>
                      {/* Description texte affichée telle quelle si présente ;
                          un lot sans description est présenté par son visuel. */}
                      {monthlyDraw.prizeDescription && (
                        <div className="text-[11px] text-muted-foreground truncate">
                          À gagner : {monthlyDraw.prizeDescription}
                        </div>
                      )}
                    </div>
                    {monthlyDraw.prizeImageUrl && (
                      <img
                        src={monthlyDraw.prizeImageUrl}
                        alt="Lot à gagner"
                        data-testid="monthly-prize-image"
                        className="h-10 w-14 flex-shrink-0 rounded-lg object-cover border border-border"
                      />
                    )}
                  </div>
                </motion.div>
              )}

              <AdCarousel
                campaigns={payload.campaigns}
                scanEventId={payload.scanEventId}
              />

              {/* Le gate disparaît une fois le compte à rebours terminé (ticket prêt). */}
              <AnimatePresence>
                {!previewMode && !gateOpen && (
                  <motion.div
                    key="ad-gate"
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0, height: 0, marginTop: 0 }}
                    transition={{ duration: 0.4 }}
                    className="overflow-hidden"
                  >
                    <AdGate
                      seconds={gateSeconds}
                      texts={texts}
                      onComplete={() => setGateOpen(true)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Slider des lots à gagner — révélé une fois la barre « Bonus
                  chance » (AdGate) terminée. */}
              <AnimatePresence>
                {gateOpen && (
                  <motion.div
                    key="prize-slider"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <PrizeSlider
                      monthlyDraw={payload.monthlyDraw || payload.draw}
                      instantDraws={payload.instantDraws}
                      texts={texts}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <motion.div
                animate={{
                  filter: gateOpen ? "blur(0px)" : "blur(5px)",
                  opacity: gateOpen ? 1 : 0.55,
                }}
                transition={{ duration: 0.45 }}
                style={{
                  pointerEvents: gateOpen ? "auto" : "none",
                  userSelect: gateOpen ? "auto" : "none",
                }}
                aria-hidden={!gateOpen}
              >
                <WinOptIn
                  scanEventId={payload.scanEventId}
                  sponsor={payload.sponsor}
                  monthlySponsor={payload.monthlySponsor}
                  draw={payload.monthlyDraw || payload.draw}
                  instantDraws={payload.instantDraws}
                  texts={texts}
                  cacheDays={branding?.clientCacheDays || 7}
                />
              </motion.div>
            </>
          )
        )}
      </main>

      <footer className="px-4 pt-2 pb-8 text-center">
        <div className="text-[10px] text-muted-foreground leading-relaxed">
          {texts.footerLegal}
        </div>
        <div className="mt-1 text-[10px] font-mono text-muted-foreground/70">
          © {new Date().getFullYear()} GoCab Boost — Pilote Abidjan
        </div>
      </footer>
    </div>
  );
}
